import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  TextField,
  TextFieldProps
} from '@mui/material'
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type InputCablesOption = {
  label: string
  value: number
}

export interface InputCablesAutocompleteProps extends Omit<
  TextFieldProps,
  'children' | 'onChange'
> {
  value: number | undefined
  onChange: (value: number | undefined) => void
}

export default function InputCablesAutocomplete({
  value,
  onChange,
  ...props
}: InputCablesAutocompleteProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('common')

  /**
   * The options.
   */
  const options: InputCablesOption[] = [
    { label: '0', value: 0 },
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 }
  ]

  /**
   * The input value.
   */
  const [inputValue, setInputValue] = useState<string>(value === undefined ? '' : `${value}`)

  /**
   * The selected option.
   */
  const selectedOption =
    (options.find((option) => option.value === value) ?? value === undefined) ? null : `${value}A`

  const handleChange = (
    _event: SyntheticEvent,
    option: string | InputCablesOption | null,
    _reason: AutocompleteChangeReason
  ) => {
    if (typeof option === 'string') {
      if (option !== '' && !isNaN(parseInt(option))) {
        onChange(parseInt(option))
      }
    } else if (option !== null) {
      onChange(option.value)
    }
  }

  const handleInputChange = (
    _event: SyntheticEvent,
    _value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // Handle the event when the user clears the input
    // using the clear button.
    if (reason === 'clear') {
      onChange(undefined)
    }
  }

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    if (value === '') {
      onChange?.(undefined)
    } else if (!isNaN(parseInt(value))) {
      onChange?.(parseInt(value))
    }
  }

  /**
   * Set the input value when value changes.
   */
  useEffect(() => {
    if (value === undefined) {
      setInputValue('')
    } else {
      setInputValue(`${value}`)
    }
  }, [value])

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      clearOnBlur
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          label={t('inputCables')}
          {...params}
          {...props}
          // inputProps={{
          //   inputMode: 'numeric'
          // }}
          onChange={handleTextFieldChange}
        />
      )}
    />
  )
}
